import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { Session, User } from '@supabase/supabase-js';
import { officeAuthService, AuthUser } from './office-auth.service';

interface AuthContextValue {
  user: AuthUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithToken: (accessToken: string, refreshToken: string) => Promise<void>;
  signInWithSSO: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function userFromSupabase(user: User): AuthUser {
  return {
    id: user.id,
    email: user.email ?? '',
    name: user.user_metadata?.full_name ?? user.email ?? '',
    avatar: user.user_metadata?.avatar_url,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check existing session on mount, then auto-attempt silent SSO
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    const init = async () => {
      try {
        // Check for an existing Supabase session
        const { session, user } = await officeAuthService.getCurrentSession();
        if (session && user) {
          setSession(session);
          setUser(userFromSupabase(user));
        }
        // No silent SSO — show the email/password login form
      } catch (err) {
        console.error('[AuthProvider] Auth init failed:', err);
      } finally {
        setLoading(false);
      }

      // Always subscribe to auth state changes (token refresh, sign out, etc.)
      try {
        const supabase = officeAuthService.getSupabase();
        const { data } = supabase.auth.onAuthStateChange(
          (_event, session) => {
            if (session) {
              setSession(session);
              setUser(userFromSupabase(session.user));
            } else {
              setSession(null);
              setUser(null);
            }
          },
        );
        subscription = data.subscription;
      } catch (err) {
        console.error('[AuthProvider] Failed to set up auth listener:', err);
      }
    };

    init();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await officeAuthService.signInWithPassword(email, password);
      setSession(result.session);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithToken = useCallback(async (accessToken: string, refreshToken: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await officeAuthService.signInWithToken(accessToken, refreshToken);
      setSession(result.session);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Token sign in failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithSSO = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // User clicked — try silent first, then NAA interactive, then dialog
      let result;
      try {
        result = await officeAuthService.signInSilent();
      } catch {
        try {
          result = await officeAuthService.signInNAAInteractive();
        } catch {
          result = await officeAuthService.signIn();
        }
      }
      setSession(result.session);
      setUser(result.user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SSO sign in failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await officeAuthService.signOut();
      setSession(null);
      setUser(null);
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    isAuthenticated: !!session && !!user,
    loading,
    error,
    signInWithPassword,
    signInWithToken,
    signInWithSSO,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
